/**
 * RedFlagSummary — top-of-candidate banner surfaced ONLY when at least one
 * uploaded employment document came back with an AI red flag or minor
 * mismatch, OR the candidate has overlapping employment dates.
 */
import { useEffect, useMemo, useState } from "react";
import { ShieldAlert, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";

const SCANNABLE = ["joining_letter", "relieving_letter", "experience_letter"];

export default function RedFlagSummary({ candidate }) {
    const [overlaps, setOverlaps] = useState([]);
    useEffect(() => {
        if (!candidate?.id) return;
        (async () => {
            try {
                const { data } = await api.get(`/candidates/${candidate.id}/concurrent-employment`);
                setOverlaps(data.overlaps || []);
            } catch { /* silent */ }
        })();
    }, [candidate?.id, candidate?.experience?.length]);

    const rows = useMemo(() => {
        const docs = candidate?.experience_documents || {};
        const out = [];
        for (const [key, bundle] of Object.entries(docs)) {
            for (const slot of SCANNABLE) {
                const item = bundle?.[slot];
                if (item?.red_flag_scan?.status && ["red_flag", "minor"].includes(item.red_flag_scan.status)) {
                    out.push({
                        key, slot,
                        label: slot.replace("_", " "),
                        status: item.red_flag_scan.status,
                        findings: item.red_flag_scan.findings || [],
                    });
                }
            }
        }
        return out;
    }, [candidate]);

    if (rows.length === 0 && overlaps.length === 0) return null;
    const redCount = rows.filter((r) => r.status === "red_flag").length;
    const minorCount = rows.length - redCount;
    const primaryRed = redCount > 0 || overlaps.length > 0;
    const Icon = primaryRed ? ShieldAlert : AlertTriangle;

    return (
        <div
            data-testid="candidate-red-flag-summary"
            className={`border rounded p-3 flex items-start gap-3 ${primaryRed ? "border-rose-300 bg-rose-50" : "border-amber-300 bg-amber-50"}`}
        >
            <Icon className={`h-5 w-5 shrink-0 mt-0.5 ${primaryRed ? "text-rose-700" : "text-amber-700"}`} />
            <div className="text-sm min-w-0 flex-1">
                <div className={`font-semibold ${primaryRed ? "text-rose-900" : "text-amber-900"}`}>
                    {redCount > 0 && `${redCount} red flag${redCount === 1 ? "" : "s"}`}
                    {redCount > 0 && (minorCount > 0 || overlaps.length > 0) && " · "}
                    {minorCount > 0 && `${minorCount} minor mismatch${minorCount === 1 ? "" : "es"}`}
                    {minorCount > 0 && overlaps.length > 0 && " · "}
                    {overlaps.length > 0 && `${overlaps.length} overlapping employment period${overlaps.length === 1 ? "" : "s"}`}
                    {" "}on this candidate.
                </div>
                <ul className="text-xs mt-1 space-y-0.5">
                    {overlaps.slice(0, 3).map((o, i) => (
                        <li key={`ov-${i}`} data-testid={`red-flag-summary-overlap-${i}`}>
                            <span className="font-medium">Concurrent employment</span>
                            {" "}— {o.a?.company} ({o.a?.start} → {o.a?.end}) overlaps {o.b?.company} ({o.b?.start} → {o.b?.end}) by {o.days_overlap} days
                        </li>
                    ))}
                    {rows.slice(0, Math.max(0, 5 - overlaps.length)).map((r, i) => (
                        <li key={i} data-testid={`red-flag-summary-row-${i}`}>
                            <span className="font-medium capitalize">{r.label}</span>
                            {r.findings.length > 0 && ` — ${r.findings[0]}`}
                        </li>
                    ))}
                    {(rows.length + overlaps.length) > 5 && (
                        <li className="italic text-muted-foreground">
                            +{(rows.length + overlaps.length) - 5} more — open the timeline to review.
                        </li>
                    )}
                </ul>
            </div>
        </div>
    );
}
